import { render, unmountComponentAtNode } from 'react-dom';

export interface OverlayCompProps<Resolve = string, Reject = unknown> {
  resolve?: (value: Resolve) => void;
  reject?: (error: Reject) => void;
  dispose?: () => void;
  [key: string]: any;
}

export type RenderOverlay<ResolveValue = string, Message = unknown> = (
  message: Message,
  time?: number,
  style?: React.CSSProperties,
  footerRef?: React.RefObject<HTMLDivElement> | null,
) => (overlayComponentProps: OverlayCompProps<ResolveValue>) => JSX.Element;

export function createOverlayComp<ReturnValue = string, Message = unknown>(
  renderComponent: RenderOverlay<ReturnValue, Message>,
  unmountDelay = 300,
  mountingNode?: HTMLElement,
): (
  props: Message,
  time?: number,
  style?: React.CSSProperties,
  footerRef?: React.RefObject<HTMLDivElement> | null,
) => Promise<ReturnValue> {
  return (
    props: Message,
    time?: number,
    style?: React.CSSProperties,
    footerRef?: React.RefObject<HTMLDivElement> | null,
  ) => {
    const wrapper = (mountingNode || document.body).appendChild(document.createElement('div'));
    const dialog = renderComponent(props, time, style, footerRef);

    function dispose() {
      setTimeout(() => {
        unmountComponentAtNode(wrapper);
        setTimeout(() => {
          if (document.body.contains(wrapper)) {
            document.body.removeChild(wrapper);
          }
        });
      }, unmountDelay);
    }

    const promise = new Promise((resolve: (value: ReturnValue) => void, reject) => {
      try {
        render(dialog({ dispose, reject, resolve }), wrapper);
      } catch (e) {
        console.error(e);
        throw e;
      }
    });

    return promise.then(
      (result) => {
        return result;
      },
      (result) => {
        dispose();
        return Promise.reject(result);
      },
    );
  };
}
